import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    Gauge,
    Clock3,
    LogOut,
    ChevronDown,
    Tickets,
    Gift,
    ScanLine, 
    QrCode,
    User
} from "lucide-react";
import { getStoreAuth } from "./StorePanelPrivateRoute";

export default function StorePanelLayout() {
    const navigate = useNavigate()
    const [profileOpen, setProfileOpen] = useState(false);

    const auth = getStoreAuth();
    const store = auth?.store;

    const navItems = [
        {
            label: "Dashboard",
            to: "/store-panel/",
            icon: <Gauge size={18} />,
        },
        {
            label: "My QR",
            to: "/store-panel/qr",
            icon: <QrCode size={18} />,
        },
        {
            label: "Pending Rewards",
            to: "/store-panel/reward-pending-ledger",
            icon: <Clock3 size={18} />,
        },
        {
            label: "Create Bill & Request Payment",
            to: "/store-panel/create-bill",
            icon: <ScanLine size={18} />,
        },
       
        {
            label: "Reward Rules",
            to: "/store-panel/reward",
            icon: <Gift size={18} />,
        },
        {
            label: "Manage Vouchers",
            to: "/store-panel/voucher",
            icon: <Tickets size={18} />,
        },
    ];

    const handleLogout = () => {
        localStorage.removeItem("store_panel_auth");
        window.location.href = "/store-login";
    };

    return (
        <div className="min-h-screen bg-gray-50">

            {/* ================= HEADER ================= */}
            <header className="w-full bg-white shadow-sm border-b flex items-center justify-between px-4 md:px-6 h-16 sticky top-0 z-40">

                {/* Goerone Logo */}
                <div className="flex items-center">
                    <img
                        src="/goerone_logo.png"
                        alt="Goerone"
                        className="h-9 w-auto object-contain"
                    />
                </div>


                {/* Store Profile */}
                <div className="relative">

                    <button
                        onClick={() => setProfileOpen(!profileOpen)}
                        className="flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-gray-50 transition"
                    >

                        {/* Store Logo */}
                        {store?.store_logo ? (
                            <img
                                src={store.store_logo}
                                alt={store?.store_name || "Store"}
                                className="h-10 w-10 rounded-lg object-cover border border-gray-200"
                            />
                        ) : (
                            <div className="h-10 w-10 rounded-lg bg-gray-100 border flex items-center justify-center">
                                <span className="text-sm font-semibold text-gray-500">
                                    {store?.store_name
                                        ?.charAt(0)
                                        ?.toUpperCase() || "S"}
                                </span>
                            </div>
                        )}

                        {/* Store Name */}
                        <div className="text-left hidden sm:block">
                            <p className="text-xs text-gray-500">
                                Store
                            </p>

                            <p className="font-semibold text-gray-800 text-sm max-w-[180px] truncate">
                                {store?.store_name || "Store"}
                            </p>
                        </div>

                        <ChevronDown
                            size={16}
                            className={`text-gray-500 transition-transform ${
                                profileOpen ? "rotate-180" : ""
                            }`}
                        />

                    </button>


                    {/* Dropdown */}
                    {profileOpen && (
                        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border py-2 text-sm z-50">

                            <div className="px-4 py-3 border-b">
                                <p className="text-xs text-gray-500">
                                    Store
                                </p>

                                <p className="font-semibold text-gray-700 truncate">
                                    {store?.store_name || "Store"}
                                </p>
                            </div>

                            <button
                                onClick={() => {
                                    setProfileOpen(false);
                                    navigate("/store-panel/profile");
                                }}
                                className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-gray-100 text-gray-700"
                            >
                                <User size={16} />
                                Manage Profile
                            </button>

                            <button
                                onClick={handleLogout}
                                className="w-full text-left flex items-center gap-2 px-4 py-2 hover:bg-gray-100 text-gray-700"
                            >
                                <LogOut size={16} />
                                Logout
                            </button>

                        </div>
                    )}

                </div>

            </header>


            {/* ================= BODY ================= */}
            <div className="flex min-h-[calc(100vh-64px)]">

                {/* ================= SIDEBAR ================= */}
                <aside className="w-64 bg-white border-r shadow-sm">

                    <nav className="p-4 space-y-1">

                        {navItems.map((item) => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.to === "/store-panel/"}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition ${
                                        isActive
                                            ? "bg-blue-50 text-blue-600"
                                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-800"
                                    }`
                                }
                            >
                                {item.icon}
                                <span>{item.label}</span>
                            </NavLink>
                        ))}

                    </nav>

                </aside>


                {/* ================= CONTENT ================= */}
                <main className="flex-1 p-6 overflow-y-auto">
                    <Outlet />
                </main>

            </div>

        </div>
    );
}